import { SharedModule } from 'src/app/shared/shared.module';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { NavigatorComponent } from './navigator/navigator.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { AppRoutingModule } from './app-routing.module';
import { StoreModule } from '@ngrx/store';
import { reducers } from './reducers';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, RouterStateSerializer } from '@ngrx/router-store';
import { CustomSerializer } from 'src/common/utils';
import { BaseModule } from './base/base.module';
import { ManagedLink } from 'src/common/managedLink';
import { SessionService } from './services/session.service';
import { AfterLoginComponent } from './session/after-login/after-login.component';
import { sessionReducer } from './session/store/session.reducer';
import { SessionEffects } from './session/store/session.effects';
import { StartSessionComponent } from './session/start-session/start-session.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from 'src/app/session/auth.interceptor';
import { LogoutComponent } from './session/logout/logout.component';
import { AuthGuard } from './session/auth.guard';
import { JsonDateInterceptor } from './config/json-date.interceptor';
import { TimeUtils } from 'src/common/timeUtils';
import { ErrorComponent } from './error/error.component';
import { ToastComponent } from './toast/toast.component';
import { ToastService } from './services/toast.service';
import { ColorRange } from 'src/common/colorRange';
import { JiraAccess } from 'src/common/jiraAccess';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MoodleAccess } from 'src/common/moodleAccess';
import { navigatorReducer } from './store/navigator/navigator.reducer';
import { NavigatorEffects } from './store/navigator/navigator.effects';
import { ConnectionTypeInterceptor } from './session/connection-type.interceptor';
import { GtagEvent } from 'src/common/gtagEvent';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EncuestaSatisfaccionComponent } from './encuesta-statisfaccion/encuesta-satisfaccion.component';
import { preguntaSatisfaccionReducer } from './store/pregunta-satisfaccion/pregunta-satisfaccion.reducer';
import { PreguntaSatisfaccionEffects } from './store/pregunta-satisfaccion/pregunta-satisfaccion.effects';
import { PreguntaSatisfaccionService } from './services/preguntaSatisfaccion.service';
import { MenuUsuarioComponent } from './menu-usuario/menu-usuario.component';
import { MenuUsuarioEffects } from './store/menu-usuario/menu-usuario.effects';
import { menuUsuarioReducer } from './store/menu-usuario/menu-usuario.reducer';
import { MenuUsuarioService } from './services/menu-usuario.service';
import { ChartsModule } from 'ng2-charts';
import { Chart } from 'chart.js';
import { ExpiracionClaveComponent } from './expiracion-clave/expiracion-clave.component';
import { TreeDraggedElement, TreeModule } from '@circlon/angular-tree-component';
import { SedeTypeInterceptor } from './session/sede-type.interceptor';
import { WithCredentialsInterceptor } from './session/with-credentials.interceptor';
import { SvgSpriteComponent } from './svg-sprite/svg-sprite.component';


@NgModule({
	declarations: [
		AppComponent,
		SidebarComponent,
		HeaderComponent,
		NavigatorComponent,
		FooterComponent,
		AfterLoginComponent,
		StartSessionComponent,
		LogoutComponent,
		ErrorComponent,
		ToastComponent,
		EncuestaSatisfaccionComponent,
		MenuUsuarioComponent,
		ExpiracionClaveComponent,
  		SvgSpriteComponent
	],
	imports: [
		AppRoutingModule,
		BrowserModule,
		BrowserAnimationsModule,
		NgbModule,
		BaseModule,
		StoreModule.forRoot(reducers),
		!environment.production ? StoreDevtoolsModule.instrument() : [],
		EffectsModule.forRoot([]),
		StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
		StoreModule.forFeature('session', sessionReducer),
		StoreModule.forFeature('navigator', navigatorReducer),
		StoreModule.forFeature('pregunta-satisfaccion', preguntaSatisfaccionReducer),
		StoreModule.forFeature('menu-usuario', menuUsuarioReducer),
		EffectsModule.forFeature([SessionEffects, NavigatorEffects, PreguntaSatisfaccionEffects, MenuUsuarioEffects]),
		SharedModule,
		FormsModule,
		ReactiveFormsModule,
		ChartsModule,
		TreeModule
	],
	providers: [
		ManagedLink,
		SessionService,
		PreguntaSatisfaccionService,
		MenuUsuarioService,
		ToastService,
		AuthGuard,
		TimeUtils,
		ColorRange,
		JiraAccess,
		MoodleAccess,
		GtagEvent,
		{ provide: RouterStateSerializer, useClass: CustomSerializer },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ConnectionTypeInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: SedeTypeInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: WithCredentialsInterceptor, multi: true },
		TreeDraggedElement
	],
	bootstrap: [
		AppComponent
	]
})
export class AppModule {

	constructor() {
		this.initChartPlugins();
	}

	// Se registra un plugin en el objeto Chart de charts.js para que dibuje los valores arriba de
	// cada barra del gráfico
	private initChartPlugins() {
		Chart.plugins.register({
			beforeDatasetsDraw: function( chartInstance ) {
				if( chartInstance.config.options["showDataPoints"] ) {
					let helpers = Chart.helpers;
					let ctx = chartInstance.ctx;
					let fontColor = helpers.getValueOrDefault( chartInstance.config.options["showDataPoints"].fontColor, chartInstance.config.options["defaultFontColor"] );

					// render the value of the chart above the bar
					ctx.font = Chart.helpers.fontString( Chart.defaults.global.defaultFontSize, 'normal', Chart.defaults.global.defaultFontFamily );
					ctx.textAlign = 'center';
					ctx.textBaseline = 'bottom';
					ctx.fillStyle = fontColor;

					chartInstance.data.datasets.forEach( function( dataset, j ) {
						let meta = chartInstance.getDatasetMeta(j);
						for( let i = 0; i < dataset.data.length; i++ ) {
							let model = meta.data[i]._model;
							let scaleMax = meta.data[i]._yScale["maxHeight"];
							let yPos = (scaleMax - model.y) / scaleMax >= 0.93? model.y + 20: model.y - 5;
							ctx.fillText( dataset.data[i].toString(), model.x, yPos );
						}
					});
				}
			}
		});
	}

}
